import { useState } from "react";
import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { llmApi } from "@/services/rag-service";
import { getApiErrorMessage } from "./utils/catalog";
import type { LLMModel } from "./types";

type AICatalogConfirmDeleteProps = {
  selectedModel: LLMModel | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const AICatalogConfirmDeleteComp = ({
  selectedModel,
  open,
  onClose,
  onSuccess,
}: AICatalogConfirmDeleteProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!selectedModel?.id) return;

    setLoading(true);
    try {
      await llmApi.deleteLLMConfig(selectedModel.id);
      toast.success(t("aiCatalog.modelDeletedSuccess"));
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error deleting model:", error);
      toast.error(getApiErrorMessage(error, t("aiCatalog.failedToDeleteModel")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      disableEscapeKeyDown={loading}
      PaperProps={{
        className:
          "w-[450px] max-w-[calc(100vw_-_32px)] rounded-lg border border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] shadow-[0_24px_80px_rgba(0,0,0,0.36)]",
      }}
    >
      <DialogTitle>{t("aiCatalog.deleteModel")}</DialogTitle>
      <DialogContent>
        <DialogContentText className="text-[var(--input-text)]">
          {t("aiCatalog.deleteModelConfirm", { name: selectedModel?.name })}
        </DialogContentText>
      </DialogContent>
      <DialogActions className="px-6 py-4">
        <Button
          data-testid="ai-catalog-delete-cancel-button"
          onClick={onClose}
          disabled={loading}
          color="secondary"
          variant="text"
        >
          {t("common.cancel")}
        </Button>
        <Button
          data-testid="ai-catalog-delete-confirm-button"
          onClick={handleDeleteConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {loading ? t("common.deleting") : t("common.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AICatalogConfirmDeleteComp;
